/** @jsx jsx */
import { IWebClient } from "@cd2/skeem-client"
import { assert, IComponentCallback } from "@cd2/tools/build/es6"
import { css, jsx } from "@emotion/core"
import { useState } from "react"
import { useHistory } from "react-router-dom"
import {
  egsBranchId,
  fortusEwBranchId,
  londonBranchId,
  sspBranchId,
} from "../../../../shared/constants"
import { IGlobalState, useGlobalState } from "../../../Components/GlobalStateProvider"
import { PageWrapper } from "../../../Components/PageWrapper"
import { icons } from "../../../constants/icons"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"
import { buildRoute, eRoutes } from "../../../routes"
import { IRecord } from "../../../types/schema"
import { IBranchSettings, useSettings } from "../../../utils/useSettings"
import { auditCreateAfterTheFact } from "../../../Components/AuditList"
import { ProductForm } from "./ProductForm"

export function ProductsNewPage() {
  const { defaultStockLocation, _loaded } = useSettings()
  const siteAlerts = useSiteAlerts()
  const history = useHistory()
  const { currentUser } = useGlobalState()
  const skeem = window.skeem

  const [initialData, setInitialData] = useState<{
    warehouse?: IRecord<"warehouses", "name">
  } | null>(null)

  async function loadInitialData() {
    if (!currentUser.branch.id || !_loaded) {
      return
    }
    const warehouse = await skeem.fetch2<IRecord<"warehouses", "name">>({
      first: {
        name: "warehouses",
        attributes: ["name"],
        filter: { matches: { name: defaultStockLocation, branch: currentUser.branch.id } },
      },
    })
    setInitialData({ warehouse: warehouse })
  }

  useAsyncEffect(async () => {
    await loadInitialData()
  }, [currentUser?.branch?.id, _loaded])

  async function handleCreateProduct(data: IComponentCallback<typeof ProductForm, "onSubmit">) {
    try {
      const newRecord = await handleSkeemCreateProduct(skeem, data, currentUser)
      await handleNotifyNewExportedProduct(skeem, newRecord.id, currentUser, {
        newProduct: !data.product_master?.id,
        exporting: !!data.exportOnSave,
      })

      siteAlerts.genericSuccess("Product created")
      history.push(buildRoute(eRoutes["/stock/products/:id"], { id: newRecord.id }))
    } catch (e) {
      console.error(e)
      siteAlerts.genericError("Error creating product")
    }
  }

  return (
    <PageWrapper
      breadcrumbPath={["stock", "products", { title: "New Product", icon: icons.edit }]}
      title={"New Product"}
    >
      <div
        css={css`
          max-width: 900px;
        `}
      >
        {initialData === null ? null : (
          <ProductForm
            onSubmit={handleCreateProduct}
            data={initialData}
            canEditProductMaster
            isNew
          />
        )}
      </div>
    </PageWrapper>
  )
}

export async function handleNotifyNewExportedProduct(
  skeem: IWebClient,
  productId: string,
  currentUser: IGlobalState["currentUser"],
  args?: { exporting?: boolean; newProduct?: boolean },
) {
  const product = await skeem.fetch2<
    IRecord<"products", "stock_code"> & {
      branch: IRecord<"branches", "name">
      product_master?: IRecord<"product_masters", "content_review_required">
    }
  >({
    first: {
      name: "products",
      attributes: [
        "stock_code",
        "sage_export_status",
        { name: "branch", attributes: ["name"] },
        {
          name: "product_master",
          attributes: ["content_review_required"],
        },
      ],
      filter: { matches: { id: productId } },
    },
  })
  // Once above exported, email the eos content team “A new code ***** has been created from the
  // product masters by **** for branch ***”
  // IF the ‘to be checked’ is checked (they’ll uncheck after the first time)

  if (args?.newProduct) {
    console.log("Send new product notification")
    // If a brand new product, always notify
    await skeem.mutate({
      alerts: {
        create: {
          type: "new-product-code",
          body: `A new product code ${product.stock_code} was created by ${currentUser.name} for branch ${product.branch.name}`,
          details: {
            product,
            user: { id: currentUser.id, name: currentUser.name, email: currentUser.email },
          },
          notifications: ["eoscontent@fortusuk.com"],
        },
      },
    })
  } else if (product.product_master?.content_review_required) {
    console.log("Send new product notification - content review required")
    // if not a new product, only notify if the product is being exported for the first time & content review is
    // required.
    await skeem.mutate({
      alerts: {
        create: {
          type: "new-product-code",
          body: `A new product code ${product.stock_code} was created by ${currentUser.name} for branch ${product.branch.name}`,
          details: {
            product,
            user: { id: currentUser.id, name: currentUser.name, email: currentUser.email },
          },
          notifications: ["eoscontent@fortusuk.com"],
        },
      },
    })
  }
}

async function handleCreateProduct(
  skeem: IWebClient,
  data: {
    barcode?: string
    orderRequestAlternativeProduct?: { id: string }
    created_by: { id: string }
    source_branch_id: string
    stock_code: string
    body: string
    manufacturerCode: string
    cost_price_pennies: number | null
    supplier: { id: string }
    supplier_part_number: string
    product_master?: { id: string }
    product_group: { id: string }
    manufacturer: { id: string }
    warehouse: { id: string; name: string }
    minimum_selling_price_pennies?: number | null
  },
) {
  assert(data.supplier?.id, "Product must have a supplier")
  assert(data.supplier_part_number?.trim(), "Product must have a supplier stock code")

  const pg = await skeem.fetch2<IRecord<"product_groups", "branch">>({
    first: {
      name: "product_groups",
      attributes: ["branch"],
      matches: { id: data.product_group.id },
    },
  })
  assert(
    data.source_branch_id == pg.branch?.id,
    "Product group must be in the same branch as the product",
  )

  const warehouse = await skeem.fetch2<IRecord<"product_groups", "branch">>({
    first: {
      name: "warehouses",
      attributes: ["branch"],
      matches: { id: data.warehouse.id },
    },
  })
  assert(
    data.source_branch_id == warehouse.branch?.id,
    "Warehouse must be in the same branch as the product",
  )

  // A product master is created as part of the product when it's not the London branch and no
  // existing master was selected. Track that so we can audit its creation after the fact below.
  const createsProductMaster = data.source_branch_id != londonBranchId && !data.product_master
  const productMasterCreateData = {
    stock_code: data.stock_code,
    name: data.body || data.stock_code,
    hidden: true,
  }

  const newRecord = await skeem.mutate<{
    id: string
    product_master: { id: string }
    stock_code: string
    body: string
  }>({
    products: {
      create: {
        importer_id: ["GENERATED", Math.random().toString()].join("-"),
        orderRequestAlternativeProduct: {
          set: data.orderRequestAlternativeProduct?.id ?? null,
        },
        barcode: data.barcode,
        created_by: { set: data.created_by?.id ?? null },
        source_branch_id: data.source_branch_id,
        stock_code: data.stock_code,
        stockCodesCache: data.stock_code,
        body: data.body,
        manufacturerCode: data.manufacturerCode,
        cost_price_pennies: data.cost_price_pennies,
        minimum_selling_price_pennies: data.minimum_selling_price_pennies,
        preferred_supplier_list_price_pennies: data.cost_price_pennies,
        branch: { set: data.source_branch_id },
        suppliers: { set: [data.supplier.id] },
        supplier_stock_codes: [
          {
            create: {
              stock_code: data.supplier_part_number,
              supplier: { set: data.supplier.id },
              source_branch_id: data.source_branch_id,
            },
          },
        ],
        preferred_supplier: { set: data.supplier.id },
        product_master:
          data.source_branch_id == londonBranchId
            ? undefined
            : data.product_master
            ? { set: data.product_master.id }
            : {
                create: productMasterCreateData,
              },
        product_group: data.product_group ? { set: data.product_group.id } : undefined,
        manufacturer: data.manufacturer ? { set: data.manufacturer.id } : undefined,
        product_locations: [
          {
            create: {
              importer_id: ["GENERATED", Math.random().toString()].join("-"),
              source_branch_id: data.source_branch_id,
              name: data.warehouse?.name ?? "GENERATED",
              warehouse: { set: data.warehouse?.id ?? null },
            },
          },
        ],
      },
    },
  })

  // If a product master was created as part of this product, record the equivalent of a create
  // audit for it (the create happened via the nested mutation above, not through auditedCreate).
  if (createsProductMaster && newRecord.product_master?.id && data.created_by?.id) {
    try {
      await auditCreateAfterTheFact(
        "product_masters",
        newRecord.product_master.id,
        productMasterCreateData,
        { id: data.created_by.id },
      )
    } catch (e) {
      // Don't fail product creation just because the audit couldn't be written
      console.error("Failed to audit product master creation", e)
    }
  }

  return newRecord
}

export async function handleSkeemCreateProduct(
  skeem: IWebClient,
  data: IComponentCallback<typeof ProductForm, "onSubmit">,
  currentUser: IGlobalState["currentUser"],
  branch?: { id: string },
) {
  const branchData = branch || currentUser.branch
  const shouldCreateFewProduct = [
    data.few_supplier ?? "",
    data.few_product_group ?? "",
    fortusEwBranchId !== branchData.id,
    sspBranchId !== branchData.id,
    egsBranchId !== branchData.id,
  ].every(Boolean)

  assert(data.warehouse, "Warehouse is required")

  const newRecord = await handleCreateProduct(skeem, {
    barcode: data.barcode,
    orderRequestAlternativeProduct: data.orderRequestAlternativeProduct,
    created_by: { id: currentUser.id! },
    source_branch_id: branchData.id!,
    stock_code: data.stock_code,
    body: data.body,
    manufacturerCode: data.manufacturerCode,
    cost_price_pennies: data.cost_price_pennies,
    supplier: { id: data.supplier },
    supplier_part_number: data.supplier_part_number,
    product_master: data.product_master,
    product_group: { id: data.product_group },
    manufacturer: data.manufacturer,
    warehouse: data.warehouse,
    minimum_selling_price_pennies: data.minimum_selling_price_pennies,
  })

  if (shouldCreateFewProduct) {
    const myBranch = await skeem.fetch2<IRecord<"branches", "is_fortus_ew_branch">>({
      first: {
        name: "branches",
        attributes: ["is_fortus_ew_branch"],
        matches: { id: branchData.id },
      },
    })
    if (myBranch.is_fortus_ew_branch) {
      return newRecord
    }

    const few = await skeem.fetch2<
      IRecord<"branches", "products" | "is_fortus_ew_branch"> & {
        settings: IBranchSettings
        warehouses: IRecord<"warehouses", "name">[]
      }
    >({
      first: {
        name: "branches",
        attributes: [
          "settings",
          "is_fortus_ew_branch",
          { name: "warehouses", attributes: ["name"] },
          {
            name: "products",
            filter: {
              or: [
                {
                  eq: [
                    { lowercase: { attr: "stock_code" } },
                    { value: data.stock_code.toLowerCase().trim() },
                  ],
                },
                data.product_master && { matches: { product_master: data.product_master?.id } },
              ].filter(Boolean),
            },
          },
        ],
        matches: { id: fortusEwBranchId },
      },
    })

    // If the product already exists or there is a selected product for the selected product master
    // then skip creating for FEW
    if (few.products.length > 0) {
      console.log("SKIPPING CREATE FEW - ALREADY EXISTS", few.products)
      return newRecord
    }

    const defaultWarehouse = few.warehouses.find(
      item => item.name.toUpperCase() === few.settings?.defaultStockLocation?.toUpperCase(),
    )
    assert(defaultWarehouse, "Default warehouse not found for FEW")
    assert(data.few_product_group, "Product group not found for FEW")
    assert(data.few_supplier, "Supplier not found for FEW")

    await handleCreateProduct(skeem, {
      created_by: { id: currentUser.id! },
      source_branch_id: fortusEwBranchId,
      stock_code: data.stock_code,
      body: data.body,
      manufacturerCode: data.manufacturerCode,
      cost_price_pennies: data.cost_price_pennies,
      supplier: { id: data.few_supplier },
      supplier_part_number: data.supplier_part_number,
      product_master: data.product_master || newRecord.product_master,
      product_group: { id: data.few_product_group },
      manufacturer: data.manufacturer,
      warehouse: defaultWarehouse,
      minimum_selling_price_pennies: data.minimum_selling_price_pennies,
    })
  }
  return newRecord
}
